import { eq } from "drizzle-orm";
import { type PowerSyncDrizzleDB, user } from "@stockpilot/powersync";

export interface UserPreferences {
  locale: string | null;
  global_theme_id: string | null;
  is_blur: number | null;
  follow_device_theme: number | null;
  font_family: string | null;
  /**
   * Avatar storage key (`avatars/{userId}/{hash}.webp`).
   *
   * It goes here and is intentionally not read from the session: the API serves
   * the session and caches it, so a newly uploaded photo would not appear until
   * the next refresh. Reading it from the local row —just like language, font,
   * and theme— makes the photo appear immediately and survive offline.
   */
  image: string | null;
}

/**
 * Shared Drizzle query builder for User preferences.
 */
export function getUserPreferencesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      locale: user.locale,
      global_theme_id: user.global_theme_id,
      is_blur: user.is_blur,
      follow_device_theme: user.follow_device_theme,
      font_family: user.font_family,
      image: user.image,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User locale.
 */
export function getUserLocaleQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      locale: user.locale,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User font family preference.
 */
export function getUserFontFamilyQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      font_family: user.font_family,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User theme settings.
 *
 * ALWAYS filters by `userId`, like all other `user` reads in this module.
 * Previously it decided the filter in one branch and the guest was left with
 * plain `SELECT … FROM user LIMIT 1`: without `WHERE`, SQLite returns the FIRST
 * row that fits, which need not be the guest's. This is not a correctness bug
 * but a data leak between accounts: during the guest→real upgrade, the local
 * database downloads one `user` row per organization co-member, so the guest
 * ended up rendering ANOTHER person's theme. Without `WHERE`, an empty result
 * is impossible: some person's row always comes back.
 */
export function getUserThemeSettingsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      global_theme_id: user.global_theme_id,
      is_blur: user.is_blur,
      follow_device_theme: user.follow_device_theme,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}
